import React, { useState } from 'react';
import { useSiteImages } from '../context/SiteImagesContext';
import { PageHeaderBanner } from '../components/PageHeaderBanner';
import { MapPin, Phone, Mail, Clock, Send, MessageCircle, CheckCircle2, ShieldCheck, ChevronDown, ChevronUp } from 'lucide-react';

interface ContactPageProps {
  onNavigate?: (page: string) => void;
}

export const ContactPage: React.FC<ContactPageProps> = ({ onNavigate }) => {
  const { images } = useSiteImages();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [productInterest, setProductInterest] = useState('Water Purification Systems');
  const [message, setMessage] = useState('');

  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'contact',
          name,
          phone,
          email,
          county: '',
          notes: `${productInterest}\n\n${message}`,
          items: []
        })
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Your inquiry could not be sent. Please try again.');
      }
      setFormSubmitted(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Your inquiry could not be sent. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppDirect = () => {
    const text = `Hello Saints Tech Solutions!%0A%0AMy name is ${name || 'Inquirer'}.%0APhone: ${phone || 'N/A'}%0AInterested in: ${productInterest}%0AMessage: ${message || 'I would like to inquire about your machinery and pricing.'}`;
    window.open(`https://wa.me/254727072393?text=${text}`, '_blank');
  };

  const faqs = [
    {
      q: 'Do you deliver and install machines outside Nairobi?',
      a: 'Yes, we deliver and offer full on-site installation, pipework, and commissioning throughout all 47 counties of Kenya (including Mombasa, Nakuru, Eldoret, Kisumu, Nyeri, Meru) as well as cross-border deliveries across East Africa (Uganda, Tanzania, Rwanda).'
    },
    {
      q: 'What is the warranty period for Saints Tech vending machines and purifiers?',
      a: 'All our new machines come with a comprehensive 12-month manufacturer warranty covering pumps, refrigeration compressors, PLC boards, and structural stainless fabrication. We also provide free on-site training upon commissioning.'
    },
    {
      q: 'Can the Milk ATM and Water Stations accept M-Pesa payments?',
      a: 'Yes! Our vending machines feature smart PLC controllers capable of dispensing by exact money amounts or volume, and can be integrated with GSM M-Pesa Paybill / Till Number readers so customers can pay directly via mobile money.'
    },
    {
      q: 'Do you help with water lab testing for KeBS certification?',
      a: 'Yes. We assist clients by facilitating comprehensive raw water chemical and microbial laboratory testing. We then custom-design your Reverse Osmosis and UV plant to ensure your purified water meets all Kenya Bureau of Standards (KeBS) potable water parameters.'
    },
    {
      q: 'What are your payment terms?',
      a: 'We accept bank transfers, RTGS, company cheques, and M-Pesa. For custom fabrication projects, we typically work on a milestone basis: deposit upon commissioning fabrication, balance upon factory inspection and delivery.'
    }
  ];

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Page Title Header with Duotone Background (matching example) */}
      <PageHeaderBanner
        breadcrumb="Contact"
        parentPage={{ label: 'Home', page: 'home' }}
        badgeIcon={<MapPin className="w-3.5 h-3.5" />}
        badgeText="Juja Road 478/B Green Mall, Nairobi, Kenya"
        badgeColor="emerald"
        title="Get in Touch With Us"
        description="We are dedicated to providing you with dependable equipment and ongoing technical support that will serve your business for years to come."
        backgroundImage={images.contactBanner}
        gradientTheme="emerald-amber"
        onNavigate={onNavigate}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16">

      {/* Main Contact Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        {/* Left Column: Official Contact Card (Screenshot Details) */}
        <div className="lg:col-span-5 space-y-8">
          <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm space-y-6">
            <h3 className="text-lg font-bold text-slate-900 pb-2 border-b border-slate-100">
              Company Directory
            </h3>

            {/* Our Offices */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Our Offices</span>
                <p className="text-sm font-semibold text-slate-900 mt-0.5">
                  Juja Road 478/B Green Mall.
                </p>
                <p className="text-xs text-slate-500">Nairobi, Kenya.</p>
              </div>
            </div>

            {/* Phone Number */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Phone Number</span>
                <p className="mt-0.5">
                  <a
                    href="tel:+254727072393"
                    className="text-base sm:text-lg font-bold text-slate-900 hover:text-sky-600 transition-colors tabular-nums"
                  >
                    +254 727 072 393
                  </a>
                </p>
                <p className="text-xs text-slate-500">Available for calls and WhatsApp messages</p>
              </div>
            </div>

            {/* Email Address */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Email Address</span>
                <p className="mt-0.5">
                  <a
                    href="mailto:info@saintstech.co.ke"
                    className="text-sm sm:text-base font-semibold text-rose-600 hover:text-rose-700 underline transition-colors"
                  >
                    info@saintstech.co.ke
                  </a>
                </p>
                <p className="text-xs text-slate-500">Official inquiries, tenders & RFQ specs</p>
              </div>
            </div>

            {/* Opening Hours */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Opening Hours</span>
                <p className="text-sm font-semibold text-slate-900 mt-0.5">
                  Monday – Friday : 9am – 5pm
                </p>
                <p className="text-xs text-slate-500 font-medium">Weekend: Closed</p>
              </div>
            </div>

            {/* Follow us on social media (Screenshot Icons) */}
            <div className="pt-4 border-t border-slate-100">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-3">
                Follow us on social media:
              </span>
              <div className="flex items-center gap-3">
                {/* Facebook */}
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Facebook"
                  className="w-10 h-10 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:opacity-90 transition-opacity shadow-xs"
                >
                  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                {/* Instagram */}
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Instagram"
                  className="w-10 h-10 rounded-full bg-[#262626] text-white flex items-center justify-center hover:opacity-90 transition-opacity shadow-xs"
                >
                  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                {/* X */}
                <a
                  href="https://x.com"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="X"
                  className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center hover:opacity-90 transition-opacity shadow-xs"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Quick WhatsApp Action Banner */}
          <div className="bg-emerald-600 text-white rounded-2xl p-6 space-y-3 shadow-sm">
            <h4 className="text-base font-bold flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Prefer Instant Chat?
            </h4>
            <p className="text-xs text-emerald-100 leading-relaxed">
              Connect directly with our sales engineering desk on WhatsApp for instant equipment photos, live video demonstrations, and immediate price quotes.
            </p>
            <a
              href="https://wa.me/254727072393?text=Hello%20Saints%20Tech%20Solutions,%20I%20would%20like%20to%20inquire%20about%20your%20products."
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-emerald-800 text-xs font-bold rounded-lg hover:bg-emerald-50 transition-colors shadow-xs"
            >
              Start WhatsApp Chat (+254 727 072 393)
            </a>
          </div>
        </div>

        {/* Right Column: Interactive Inquiry Form */}
        <div className="lg:col-span-7 bg-white rounded-3xl p-6 sm:p-10 border border-slate-200 shadow-sm">
          {formSubmitted ? (
            <div className="py-12 text-center space-y-4">
              <div className="w-14 h-14 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900">
                Message Sent Successfully!
              </h3>
              <p className="text-sm text-slate-600 max-w-md mx-auto leading-relaxed">
                Thank you, <span className="font-semibold text-slate-900">{name}</span>. Our technical representatives have received your message regarding <span className="font-semibold text-slate-900">{productInterest}</span> and will respond to <span className="font-semibold text-slate-900">{phone}</span> promptly during working hours.
              </p>
              <div className="pt-4 flex justify-center gap-3">
                <button
                  onClick={handleWhatsAppDirect}
                  className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg flex items-center gap-2"
                >
                  <MessageCircle className="w-4 h-4" />
                  Also Send to WhatsApp
                </button>
                <button
                  onClick={() => setFormSubmitted(false)}
                  className="px-5 py-2.5 text-xs font-semibold text-slate-700 hover:text-slate-900"
                >
                  Send Another Inquiry
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-slate-900">
                  Send Us a Direct Message
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Fill in your details below and our team will get back to you with custom configurations.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Your Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Dennis Kipchumba"
                    className="w-full text-xs sm:text-sm px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Phone Number (Calls & WhatsApp) *
                  </label>
                  <input
                    type="tel"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+254 7XX XXX XXX"
                    className="w-full text-xs sm:text-sm px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@example.com"
                    className="w-full text-xs sm:text-sm px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Equipment of Interest
                  </label>
                  <select
                    value={productInterest}
                    onChange={(e) => setProductInterest(e.target.value)}
                    className="w-full text-xs sm:text-sm px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-sky-500 cursor-pointer"
                  >
                    <option value="Water Purification Systems">Water Purification Systems (RO)</option>
                    <option value="Milk ATM Machines">Milk ATM Machines (100L / 200L)</option>
                    <option value="Water Vending Machines">Water Vending Stations & Taps</option>
                    <option value="Cooking Oil ATMs">Cooking Oil ATMs / Salad ATMs</option>
                    <option value="Milk Pasteurizers">300L Batch Pasteurizer</option>
                    <option value="Servicing & Spare Parts">Servicing & Spare Parts</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Your Requirements or Location Details
                </label>
                <textarea
                  rows={4}
                  required
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell us about your project: e.g. Starting a water refilling station in Nakuru, source is borehole water with 800 TDS. Need quotation for 500L/hr RO plant and 3-tap dispensing booth..."
                  className="w-full text-xs sm:text-sm px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                />
              </div>

              {submitError && (
                <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  {submitError}
                </p>
              )}

              <div className="pt-2 flex flex-col sm:flex-row gap-3">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-3 px-5 bg-sky-600 hover:bg-sky-700 active:bg-sky-800 text-white text-xs font-bold rounded-xl shadow-sm transition-colors cursor-pointer flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  <span>{isSubmitting ? 'Sending...' : 'Send Official Inquiry'}</span>
                </button>
                <button
                  type="button"
                  onClick={handleWhatsAppDirect}
                  className="py-3 px-5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-colors flex items-center justify-center gap-2 cursor-pointer"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>Quick WhatsApp Inquire</span>
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Office Map & Landmark Guide */}
      <div className="bg-slate-50 rounded-3xl p-6 sm:p-10 border border-slate-200 space-y-6">
        <div className="max-w-2xl">
          <div className="text-xs font-bold uppercase tracking-wider text-sky-600">
            Directions & Landmark
          </div>
          <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">
            Finding Our Workshop at Juja Road Green Mall
          </h2>
          <p className="text-xs sm:text-sm text-slate-600 mt-1">
            Conveniently accessible along Juja Road in Nairobi with ample secure parking for vehicles and loading trucks.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-5 rounded-2xl border border-slate-200 space-y-2">
            <span className="text-xs font-bold text-sky-700">From Nairobi CBD</span>
            <p className="text-xs text-slate-600 leading-relaxed">
              Take Juja Road past Pangani and Mlango Kubwa heading towards Eastleigh/Huruma. Green Mall is on the main corridor at Plot 478/B.
            </p>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200 space-y-2">
            <span className="text-xs font-bold text-sky-700">From Thika Superhighway</span>
            <p className="text-xs text-slate-600 leading-relaxed">
              Exit at Pangani interchange or Outer Ring Road intersection and join Juja Road directly. Convenient for client pickups from Kiambu, Thika, or Murang'a.
            </p>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200 space-y-2">
            <span className="text-xs font-bold text-sky-700">Machine Pickup & Loading</span>
            <p className="text-xs text-slate-600 leading-relaxed">
              Our loading bay accommodates pickup trucks and canters. We assist with secure loading, strapping, and transit documentation.
            </p>
          </div>
        </div>
      </div>

      {/* Frequently Asked Questions */}
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="text-center space-y-1">
          <div className="text-xs font-bold uppercase tracking-wider text-sky-600">
            Got Questions?
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            Frequently Asked Questions
          </h2>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, idx) => {
            const isOpen = openFaq === idx;
            return (
              <div
                key={idx}
                className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(isOpen ? null : idx)}
                  className="w-full p-4 sm:p-5 text-left font-bold text-sm sm:text-base text-slate-900 flex items-center justify-between gap-4 hover:bg-slate-50 transition-colors"
                >
                  <span>{faq.q}</span>
                  {isOpen ? (
                    <ChevronUp className="w-4 h-4 text-sky-600 shrink-0" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                  )}
                </button>
                {isOpen && (
                  <div className="px-4 sm:px-5 pb-5 text-xs sm:text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3">
                    {faq.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      </div>
    </div>
  );
};
